import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, IconButton, Paper, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { DocumentUploadDialog } from './DocumentUploadDialog';
import { documentAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  type DocumentRecord,
  type VehicleDocSlot,
  VEHICLE_DOCUMENT_TYPES,
  formatDocExpiry,
  getExpiryStatusMeta,
  normalizeExpiryStatus,
} from '../../constants/documentTypes';
import { RADIUS, brandCss } from '../../theme/appTheme';

interface VehicleDocumentChecklistProps {
  vehicleId: string;
  vehicleNumber?: string;
}

function buildSlots(docs: DocumentRecord[]): VehicleDocSlot[] {
  const sorted = [...docs].sort((a, b) => (
    new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime()
  ));
  return VEHICLE_DOCUMENT_TYPES.map((type) => {
    const typeDocs = sorted.filter((d) => d.documentType === type.value);
    const document = typeDocs[0] || null;
    return {
      documentType: type.value,
      label: type.label,
      required: type.required !== false,
      status: document ? normalizeExpiryStatus(document.expiryStatus) : 'missing',
      document,
      documents: typeDocs,
    };
  });
}

export const VehicleDocumentChecklist: React.FC<VehicleDocumentChecklistProps> = ({
  vehicleId,
  vehicleNumber,
}) => {
  const { hasPermission } = useAuth();
  const [docs, setDocs] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDoc, setEditDoc] = useState<DocumentRecord | null>(null);
  const [presetType, setPresetType] = useState('');

  const canCreate = hasPermission('documents', 'create');
  const canEdit = hasPermission('documents', 'edit');

  const load = useCallback(async () => {
    if (!vehicleId) return;
    try {
      setLoading(true);
      setError('');
      const res = await documentAPI.list({ entityType: 'vehicle', entityId: vehicleId });
      setDocs(res.data || []);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to load vehicle documents');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => { load(); }, [load]);

  const slots = useMemo(() => buildSlots(docs), [docs]);

  const handleView = async (doc?: DocumentRecord | null) => {
    if (!doc?._id) return;
    try {
      const blob = await documentAPI.downloadFile(doc._id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError('Failed to open document');
    }
  };

  const openUpload = (slot: VehicleDocSlot, asNew = false) => {
    // Supportive docs: always allow adding another file
    const forceNew = asNew || (slot.documentType === 'supportive' && !asNew && Boolean(slot.document));
    setEditDoc(forceNew ? null : (slot.document || null));
    setPresetType(slot.documentType);
    setDialogOpen(true);
  };

  const counts = useMemo(() => {
    const required = slots.filter((s) => s.required);
    return {
      expired: required.filter((s) => s.status === 'expired').length,
      expiring7: required.filter((s) => s.status === 'expiring_7').length,
      expiring30: required.filter((s) => s.status === 'expiring_30').length,
      valid: required.filter((s) => s.status === 'valid' || s.status === 'no_expiry').length,
      missing: required.filter((s) => s.status === 'missing').length,
    };
  }, [slots]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography fontWeight={700} fontSize={18}>Vehicle documents</Typography>
        {canCreate && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => { setEditDoc(null); setPresetType('rc'); setDialogOpen(true); }}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS}px`, background: brandCss.gradientPrimary }}
            >
              Add document
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => { setEditDoc(null); setPresetType('supportive'); setDialogOpen(true); }}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS}px` }}
            >
              Supportive document
            </Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {[
          { key: 'expired', label: '🔴 Expired', value: counts.expired },
          { key: '7', label: '🟠 Within 7 days', value: counts.expiring7 },
          { key: '30', label: '🟡 Within 30 days', value: counts.expiring30 },
          { key: 'valid', label: '🟢 Valid', value: counts.valid },
        ].map((item) => (
          <Chip key={item.key} label={`${item.label}  ${item.value}`} size="small" sx={{ fontWeight: 700 }} />
        ))}
        {counts.missing > 0 && (
          <Chip label={`Not uploaded  ${counts.missing}`} size="small" sx={{ fontWeight: 600 }} />
        )}
      </Box>

      {loading && !docs.length ? (
        <Typography color="text.secondary">Loading documents…</Typography>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
          gap: 1.25,
        }}>
          {slots.map((slot) => {
            const meta = getExpiryStatusMeta(slot.status);
            const primary = slot.document;
            return (
              <Paper
                key={slot.documentType}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${meta.border}`,
                  bgcolor: `${meta.bg}55`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Box>
                    <Typography fontWeight={800} fontSize={14}>{slot.label}</Typography>
                    <Chip
                      label={`${meta.emoji} ${meta.label}`}
                      size="small"
                      sx={{ mt: 0.5, height: 22, fontSize: 11, fontWeight: 700, bgcolor: meta.bg, color: meta.color }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.25 }}>
                    {primary?._id && (
                      <Tooltip title="View file">
                        <IconButton size="small" onClick={() => handleView(primary)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(canEdit || canCreate) && (
                      <Tooltip title={primary ? 'Edit' : 'Upload'}>
                        <IconButton size="small" onClick={() => openUpload(slot)} color="primary">
                          {primary ? <EditIcon fontSize="small" /> : <CloudUploadIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {primary ? (
                  <Box sx={{ mt: 1.25 }}>
                    {(slot.documents?.length || 0) > 1 && (
                      <Typography fontSize={11} color="text.secondary">
                        {slot.documents?.length} files uploaded
                      </Typography>
                    )}
                    <Typography fontSize={12} color="text.secondary">
                      Issue: {formatDocExpiry(primary.issueDate)}
                    </Typography>
                    <Typography fontSize={13} fontWeight={700} sx={{ color: meta.color, mt: 0.25 }}>
                      {slot.label} expires: {formatDocExpiry(primary.expiryDate)}
                    </Typography>
                    {slot.documentType === 'supportive' && canCreate && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => openUpload(slot, true)}
                        sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}
                      >
                        Add another
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Typography fontSize={12} color="text.secondary" sx={{ mt: 1.25 }}>
                    {slot.required ? 'Not uploaded yet' : 'Optional — upload if needed'}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      )}

      <DocumentUploadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
        entityType="vehicle"
        entityId={vehicleId}
        entityLabel={vehicleNumber}
        editDoc={editDoc}
        presetDocumentType={presetType}
      />
    </Box>
  );
};
